import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, HelpCircle, Loader2, Lock } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { FPED_TABS, TABS, tabOf } from "../lib/portalGroupTabs";
import { introSeen, markIntroSeen, participantsLine } from "../lib/portalHelp";
import { Shell } from "../components/portal/PortalShell";
import GroupAgenda from "../components/portal/GroupAgenda";
import FeedbackCard from "../components/portal/FeedbackCard";
import ParticipantsTab from "../components/portal/ParticipantsTab";
import DocumentsTab from "../components/portal/DocumentsTab";
import PresenceBoard from "../components/portal/PresenceBoard";
import SimpleAttendance from "../components/portal/SimpleAttendance";
import PortfolioBoard from "../components/portal/PortfolioBoard";
import GroupIntro from "../components/portal/GroupIntro";
import Hint from "../components/portal/Hint";
import { isFped } from "../lib/regime";

/**
 * `/portal/grupa/:groupId` — the trainer's page for one group (06.10.2026).
 * It opens on where the group is and what to do now (the agenda); below,
 * four tabs — Cursanți, Prezență, Portofolii, Documente — in `?fila=`, so a
 * link from the agenda lands on the right one.
 */
export default function PortalGroup() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [roster, setRoster] = useState(null);
  // A Curs FPED cohort: the participants with their assignments, and a simple attendance.
  const fped = isFped(roster?.group);
  const tabs = fped ? FPED_TABS : TABS;
  const tab = tabOf(params.get("fila"), tabs);
  const [hours, setHours] = useState(null);
  const [presence, setPresence] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [error, setError] = useState("");
  const [intro, setIntro] = useState(() => !introSeen(groupId));
  const closeIntro = () => { markIntroSeen(groupId); setIntro(false); };
  useEffect(() => { setIntro(!introSeen(groupId)); }, [groupId]);

  const load = useCallback(async () => {
    try {
      const fresh = (await api.get(`/portal/groups/${groupId}/roster`)).data;
      setRoster(fresh);
      // A Curs FPED cohort keeps no attendance (12.10.2026): nothing to ask for.
      if (!isFped(fresh.group)) {
        api.get(`/portal/groups/${groupId}/attendance`).then((r) => setHours(r.data)).catch(() => setHours(null));
        api.get(`/portal/groups/${groupId}/presence`).then((r) => setPresence(r.data)).catch(() => setPresence(null));
        api.get(`/portal/groups/${groupId}/initial-assessment`).then((r) => setAssessment(r.data))
          .catch(() => setAssessment(null));
      }
      api.get(`/portal/groups/${groupId}/agenda`).then((r) => setAgenda(r.data)).catch(() => setAgenda(null));
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am găsit grupa."));
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  // A link from the agenda opens a tab further down: bring it into view.
  const tabsRef = useRef(null);
  const asked = params.get("fila");
  useEffect(() => {
    if (!asked || !roster) return undefined;
    // After the app's own scroll-to-top on a new address.
    const t = setTimeout(() => {
      const el = tabsRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [asked, roster]);

  const pick = (key) => {
    const next = new URLSearchParams(params);
    if (key === TABS[0].key) next.delete("fila"); else next.set("fila", key);
    setParams(next, { replace: true });
  };
  const openParticipant = (eid) => navigate(`/portal/grupa/${groupId}/cursant/${eid}`);

  return (
    <Shell wide>
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Înapoi la grupe
      </Link>
      {error && <p className="mt-8 text-sm text-[#c0483f]" role="alert">{error}</p>}
      {!roster && !error && <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
      {roster && (
        <>
          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">Grupa ta</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#373A45]">{roster.group.name}</h1>
          <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#6B7280]">
            <span data-testid="portal-participants-line">
              {participantsLine(roster.participants.length, !agenda || Boolean(agenda.participants_opened_at))}
            </span>
            {!intro && (
              <button type="button" onClick={() => setIntro(true)} data-testid="portal-intro-open"
                className="inline-flex items-center gap-1 font-semibold text-[#7A4986] hover:text-[#965EA7]">
                <HelpCircle size={14} /> Cum folosești pagina
              </button>
            )}
          </p>

          {intro && <GroupIntro stage={agenda?.stage} onClose={closeIntro} fped={fped} />}

          <GroupAgenda agenda={agenda} groupId={groupId}
            onChanged={() => api.get(`/portal/groups/${groupId}/agenda`).then((r) => setAgenda(r.data)).catch(() => {})} />

          {/* The final feedback (30.10.2026): the link for the last session, then the results. */}
          <FeedbackCard groupId={groupId} />

          <div ref={tabsRef} className="mt-10 scroll-mt-4 border-b border-[#E5E7EB]" role="tablist" aria-label="Despre grupă">
            <div className="-mb-px flex gap-1 overflow-x-auto">
              {tabs.map((t) => (
                <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
                  aria-controls={`portal-tab-${t.key}`} id={`portal-tabbtn-${t.key}`} onClick={() => pick(t.key)}
                  className={`shrink-0 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                    tab === t.key ? "border-[#965EA7] text-[#7A4986]" : "border-transparent text-[#6B7280] hover:text-[#373A45]"}`}
                  data-testid={`portal-tab-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5" role="tabpanel" id={`portal-tab-${tab}`} aria-labelledby={`portal-tabbtn-${tab}`}>
            {tab === "cursanti" && (
              <ParticipantsTab roster={roster} hours={hours} presence={presence} groupId={groupId} onOpen={openParticipant}
                fped={fped} assessment={assessment} openProfile={params.get("profil") === "1"} />
            )}
            {tab === "prezenta" && fped && (
              // A Curs FPED cohort: who was at each session, and the 80% of the contract.
              <SimpleAttendance base="/portal" groupId={groupId}
                onChanged={() => api.get(`/portal/groups/${groupId}/agenda`).then((r) => setAgenda(r.data)).catch(() => {})} />
            )}
            {tab === "prezenta" && !fped && (
              <>
                <TabIntro term="threshold">
                  După fiecare sesiune marchezi prezența și atașezi raportul din Meet. Aici vezi și frecvența fiecărui
                  cursant și generezi anexele de prezență.
                </TabIntro>
                <PresenceBoard base="/portal" groupId={groupId} onOpenParticipant={openParticipant} />
              </>
            )}
            {tab === "portofolii" && (
              agenda && ["before", "course"].includes(agenda.stage) ? (
                <p className="flex items-start gap-2 rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm text-[#6B7280]"
                  data-testid="portal-portfolios-later">
                  <Lock size={16} className="mt-0.5 shrink-0" />
                  <span>
                    Fila se deschide după ultima sesiune a cursului. Atunci verifici aici structura fiecărui portofoliu
                    depus, pe cele 7 elemente, și generezi Anexa 6.
                  </span>
                </p>
              ) : (
                <>
                  <TabIntro term="structure">
                    Pentru fiecare portofoliu depus: DA sau NU la cele 7 elemente. La „respins”, scrie ce lipsește.
                  </TabIntro>
                  <PortfolioBoard base="/portal" groupId={groupId} />
                </>
              )
            )}
            {tab === "documente" && <DocumentsTab presence={presence} groupName={roster.group.name} />}
          </div>
        </>
      )}
    </Shell>
  );
}


function TabIntro({ term, children }) {
  return (
    <p className="mb-5 flex items-start gap-1.5 text-sm text-[#6B7280]">
      <span>{children}</span>
      <Hint term={term} align="right" />
    </p>
  );
}
